'use client';

import { Box, Heading, HStack, Stack } from '@chakra-ui/react';
import { PageContent } from '../ui/page/content';
import { SearchTab } from '@habity/types';
import { usePathUtils } from '~/hooks/usePathUtils';
import { ROUTES } from '@habity/constants';
import { SearchListTabs } from '../ui/tabs/SearchListTabs';
import { AutocompleteInput } from '../ui/forms/Autocomplete';
import { useFilters } from '~/hooks/useFilters';
import { SuggestionTabs } from '../ui/tabs/SuggestionTabs';
import { AnimatePresence, motion } from 'framer-motion';
import { useHomesStore } from '~/store';
import { useSearchParams } from 'next/navigation';

const MotionHeading = motion(Heading);

export function SearchHero() {
  const { isExactPath } = usePathUtils();
  const { performSearch } = useFilters();
  const count = useHomesStore((state) => state.count);
  const searchParams = useSearchParams();
  const getSearchTab = (): SearchTab | null => {
    if (isExactPath(ROUTES.SEARCH.HOMES)) return 'homes';
    if (isExactPath(ROUTES.SEARCH.SPECULATORS)) return 'speculators';
    if (isExactPath(ROUTES.SEARCH.BROKERS)) return 'brokers';
    if (isExactPath(ROUTES.SEARCH.MUNICIPALITIES)) return 'municipalities';
    if (isExactPath(ROUTES.SEARCH.AREAS)) return 'areas';
    if (isExactPath(ROUTES.SEARCH.PLACES)) return 'places';
    if (isExactPath(ROUTES.SEARCH.PROJECTS)) return 'projects';
    return null;
  };

  const searchTab = getSearchTab();
  const isAreaTypeSearch = searchTab === 'areas' || searchTab === 'places';
  const showSuggestions =
    isExactPath(ROUTES.SEARCH.HOMES) || isExactPath(ROUTES.SEARCH.SPECULATORS);

  if (!searchTab) return null;

  return (
    <PageContent containerProps={{ py: 0 }} py={0} position="relative">
      {/* Top spacer which also acts as a cutoff for the sliding up word in the animation */}
      <Box
        h={{ mobile: '6', desktop: '16' }}
        backgroundColor="white"
        zIndex="2"
        position="relative"
      />

      {/* Page title: Second word is animated on tab change */}
      <Stack
        justifyContent="center"
        mb={{ mobile: '3', desktop: '10' }}
        pb={{ mobile: '3', desktop: '6' }}
        overflow="hidden"
        position="relative"
      >
        <HStack spacing={{ mobile: '2', desktop: '4' }} position="relative">
          <Heading size={{ mobile: '3xl', desktop: '5xl' }}>Hitta</Heading>
          <AnimatePresence mode="popLayout">
            <MotionHeading
              key={searchTab}
              size={{ mobile: '3xl', desktop: '5xl' }}
              initial={{ y: 100, opacity: 1 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -100, opacity: 1 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              flex="1"
              position="relative"
              zIndex="1"
            >
              {getTabTitle(searchTab)}
            </MotionHeading>
          </AnimatePresence>
        </HStack>
      </Stack>

      <Stack
        spacing="0"
        mt="0"
        zIndex="5" // Search dropdown should render on top of page content
      >
        {/* Hide tabs on municipalities and areas pages because they're handled further down in a different component */}
        {!isAreaTypeSearch && (
          <SearchListTabs
            tab={searchTab}
            useParamBasedTabs={false}
            onTabChange={(newTab) => {
              performSearch({
                tab: newTab,
                includeExistingFilters: true,
              });
            }}
            showSearchTabs={[
              'homes',
              'speculators',
              'brokers',
              'projects',
              'municipalities',
            ]}
          />
        )}

        <Stack pb={showSuggestions ? 0 : 2}>
          <AutocompleteInput
            showFiltersButton
            showLocationButton
            tab={searchTab}
            hideNavigation={true}
            showMessageButton={searchTab === 'brokers'}
            messageQueryParams={searchParams?.toString() || ''}
            messageRecipientCount={
              searchTab === 'brokers'
                ? count.brokers || 0
                : count.speculatorsUnique || count.speculators || 0
            }
            showTestMatchingButton={searchTab === 'homes'}
          />
          <SuggestionTabs />
        </Stack>
      </Stack>
    </PageContent>
  );
}

function getTabTitle(tab: SearchTab) {
  switch (tab) {
    case 'homes':
      return 'bostad';
    case 'speculators':
      return 'spekulanter';
    case 'brokers':
      return 'mäklare';
    case 'municipalities':
      return 'område';
    case 'areas':
      return 'område';
    case 'places':
      return 'plats';
    case 'projects':
      return 'nyproduktion';
    default:
      return '';
  }
}
